/**
 * Centralized utilities for preview environment detection and OAuth redirect handling.
 *
 * Preview environments use URL patterns like:
 * - Partner: pr-3187.app.staging.alternativepayments.io
 * - Admin:   pr-3187-admin.staging.alternativepayments.io
 * - Auth:    pr-3187-auth.staging.alternativepayments.io
 * - Payments: partner-pr3187.staging.alternativepayments.io (partner-specific wildcard)
 */

/**
 * App types that support preview environments
 */
export enum PreviewAppType {
  Partner = 'partner',
  Admin = 'admin',
  Auth = 'auth',
  Payments = 'payments'
}

/**
 * Preview environment detection patterns for each app type
 */
const PREVIEW_PATTERNS: Record<PreviewAppType, RegExp> = {
  // pr-3187.app.staging.alternativepayments.io
  [PreviewAppType.Partner]: /^pr-(\d+)\.app\.staging\.alternativepayments\.io$/,
  // pr-3187-admin.staging.alternativepayments.io
  [PreviewAppType.Admin]: /^pr-(\d+)-admin\.staging\.alternativepayments\.io$/,
  // pr-3187-auth.staging.alternativepayments.io
  [PreviewAppType.Auth]: /^pr-(\d+)-auth\.staging\.alternativepayments\.io$/,
  // partner-pr3187.staging.alternativepayments.io (hyphenated format)
  [PreviewAppType.Payments]:
    /^[\w-]+-pr(\d+)\.staging\.alternativepayments\.io$/
}

/**
 * Checks if a hostname is a preview environment for any app type
 *
 * @param hostname - The hostname to check (e.g., window.location.hostname or req.headers.host)
 * @returns true if the hostname matches any preview environment pattern
 *
 * @example
 * isPreviewEnvironment('pr-3187.app.staging.alternativepayments.io') // true
 * isPreviewEnvironment('app.staging.alternativepayments.io') // false
 */
export const isPreviewEnvironment = (hostname: string): boolean => {
  if (!hostname) return false
  return Object.values(PREVIEW_PATTERNS).some(pattern => pattern.test(hostname))
}

/**
 * Checks if a hostname is a preview environment for a specific app type
 *
 * @param hostname - The hostname to check
 * @param appType - The app type to check for
 * @returns true if the hostname matches the preview pattern for the specified app
 *
 * @example
 * isPreviewEnvironmentForApp('pr-3187.app.staging.alternativepayments.io', PreviewAppType.Partner) // true
 * isPreviewEnvironmentForApp('pr-3187.app.staging.alternativepayments.io', PreviewAppType.Admin) // false
 */
export const isPreviewEnvironmentForApp = (
  hostname: string,
  appType: PreviewAppType
): boolean => {
  if (!hostname) return false
  return PREVIEW_PATTERNS[appType].test(hostname)
}

/**
 * Extracts the PR number from a preview environment hostname
 *
 * @param hostname - The preview environment hostname
 * @returns The PR number as a string, or null if not a preview environment
 *
 * @example
 * getPreviewPRNumber('pr-3187.app.staging.alternativepayments.io') // '3187'
 * getPreviewPRNumber('app.staging.alternativepayments.io') // null
 */
export const getPreviewPRNumber = (hostname: string): string | null => {
  if (!hostname) return null

  for (const pattern of Object.values(PREVIEW_PATTERNS)) {
    const match = hostname.match(pattern)
    if (match) {
      return match[1]
    }
  }

  return null
}

/**
 * Gets the OAuth callback redirect URI for preview environments.
 * For non-preview environments, returns undefined to use backend defaults.
 *
 * NOTE: Payments preview environments return undefined because each partner
 * subdomain would need its own redirect URI registered in Ory, which is not
 * scalable. Payments preview uses the backend's default redirect handling.
 *
 * @param hostname - The current hostname
 * @param protocol - The protocol (defaults to 'https')
 * @param callbackPath - The callback path (defaults to '/callback')
 * @returns The full redirect URI for partner/admin previews, or undefined for non-preview/payments
 *
 * @example
 * // Partner preview environment
 * getPreviewRedirectUri('pr-3187.app.staging.alternativepayments.io')
 * // Returns: 'https://pr-3187.app.staging.alternativepayments.io/callback'
 *
 * // Payments preview environment (returns undefined - uses backend default)
 * getPreviewRedirectUri('ribeiro-pr3187.staging.alternativepayments.io')
 * // Returns: undefined
 *
 * // Non-preview environment
 * getPreviewRedirectUri('app.staging.alternativepayments.io')
 * // Returns: undefined
 */
export const getPreviewRedirectUri = (
  hostname: string,
  protocol = 'https',
  callbackPath = '/callback'
): string | undefined => {
  if (!hostname) return undefined

  // Only return custom redirect URI for partner and admin previews
  // Payments preview uses dynamic partner subdomains that can't be pre-registered
  const isPartnerPreview = isPreviewEnvironmentForApp(
    hostname,
    PreviewAppType.Partner
  )
  const isAdminPreview = isPreviewEnvironmentForApp(
    hostname,
    PreviewAppType.Admin
  )

  if (!isPartnerPreview && !isAdminPreview) {
    return undefined
  }

  return `${protocol}://${hostname}${callbackPath}`
}

/**
 * Gets the preview auth domain URL for a given PR number.
 * The auth app uses the pattern: pr-{number}-auth.staging.alternativepayments.io
 *
 * @param prNumber - The PR number
 * @returns The preview auth domain URL
 *
 * @example
 * getPreviewAuthDomain('3187')
 * // Returns: 'https://pr-3187-auth.staging.alternativepayments.io'
 */
export const getPreviewAuthDomain = (prNumber: string): string =>
  `https://pr-${prNumber}-auth.staging.alternativepayments.io`

/**
 * Gets the preview partner app domain URL for a given PR number.
 *
 * @param prNumber - The PR number
 * @returns The preview partner domain URL
 *
 * @example
 * getPreviewPartnerDomain('3187')
 * // Returns: 'https://pr-3187.app.staging.alternativepayments.io'
 */
export const getPreviewPartnerDomain = (prNumber: string): string =>
  `https://pr-${prNumber}.app.staging.alternativepayments.io`

/**
 * Gets the preview admin app domain URL for a given PR number.
 *
 * @param prNumber - The PR number
 * @returns The preview admin domain URL
 *
 * @example
 * getPreviewAdminDomain('3187')
 * // Returns: 'https://pr-3187-admin.staging.alternativepayments.io'
 */
export const getPreviewAdminDomain = (prNumber: string): string =>
  `https://pr-${prNumber}-admin.staging.alternativepayments.io`

/**
 * Gets the partner app URL for the current environment.
 * For admin preview environments, returns the corresponding partner preview URL.
 * For non-preview environments, returns the fallback URL.
 *
 * @param hostname - The current hostname (e.g., window.location.hostname)
 * @param fallbackUrl - The fallback URL for non-preview environments (e.g., NEXT_PUBLIC_PARTNER_DOMAIN)
 * @returns The partner app URL
 *
 * @example
 * // From admin preview
 * getPartnerAppUrl('pr-3338-admin.staging.alternativepayments.io', 'https://app.staging...')
 * // Returns: 'https://pr-3338.app.staging.alternativepayments.io'
 *
 * @example
 * // From non-preview
 * getPartnerAppUrl('admin.staging.alternativepayments.io', 'https://app.staging...')
 * // Returns: 'https://app.staging...'
 */
export const getPartnerAppUrl = (
  hostname: string,
  fallbackUrl: string
): string => {
  if (hostname?.includes('-admin.staging.alternativepayments.io')) {
    return `https://${hostname.replace('-admin.staging', '.app.staging')}`
  }
  return fallbackUrl
}

/**
 * Client-side helper to get the redirect URI from the current window location.
 * Safe to use in React components and hooks.
 *
 * @returns The redirect URI for preview environments, or undefined for non-preview
 *
 * @example
 * // In a React component
 * const redirectUri = getClientRedirectUri()
 * const { data } = useOAuth2UrlQuery({ variables: { redirectUri } })
 */
export const getClientRedirectUri = (): string | undefined => {
  if (typeof window === 'undefined') return undefined
  return getPreviewRedirectUri(window.location.hostname)
}

/**
 * Server-side helper to get the redirect URI from the request host.
 * Safe to use in getServerSideProps.
 *
 * @param host - The request host header (req.headers.host)
 * @returns The redirect URI for preview environments, or undefined for non-preview
 *
 * @example
 * // In getServerSideProps
 * const host = req.headers.host
 * const redirectUri = getServerRedirectUri(host)
 */
export const getServerRedirectUri = (
  host: string | undefined
): string | undefined => {
  if (!host) return undefined
  return getPreviewRedirectUri(host)
}

// ===== DOMAIN TRANSFORMATION UTILITIES =====

/**
 * Transform preview domain to staging domain for backend/database lookup.
 *
 * Supports both formats:
 * - OLD FORMAT (nested subdomain): partner.pr-3187.staging.alternativepayments.io
 * - NEW FORMAT (hyphenated): partner-pr3187.staging.alternativepayments.io
 *
 * Both transform to: partner.staging.alternativepayments.io
 *
 * The new hyphenated format is preferred because wildcard SSL certs (*.staging.alternativepayments.io)
 * only cover one subdomain level. The old nested format caused SSL errors.
 *
 * @param domain - The domain to transform
 * @returns Transformed domain (or original if not a preview domain)
 *
 * @example
 * // New hyphenated format
 * transformPreviewDomain('ribeiro-pr3187.staging.alternativepayments.io')
 * // Returns: 'ribeiro.staging.alternativepayments.io'
 *
 * @example
 * // Old nested format (backwards compatibility)
 * transformPreviewDomain('ribeiro.pr-3187.staging.alternativepayments.io')
 * // Returns: 'ribeiro.staging.alternativepayments.io'
 *
 * @example
 * // Non-preview domain (unchanged)
 * transformPreviewDomain('ribeiro.staging.alternativepayments.io')
 * // Returns: 'ribeiro.staging.alternativepayments.io'
 */
export const transformPreviewDomain = (domain: string): string => {
  if (!domain) return domain

  const parts = domain.split('.')

  // Check if domain ends with alternativepayments.io (only our domains)
  if (!domain.endsWith('alternativepayments.io')) {
    return domain
  }

  // NEW FORMAT: Hyphenated preview domains (4+ parts)
  // Pattern: partner-pr<number>.staging.alternativepayments.io
  // Example: ribeiro-pr3187.staging.alternativepayments.io
  if (parts.length >= 4) {
    const firstPart = parts[0]
    const prIndex = firstPart.lastIndexOf('-pr')

    if (prIndex > 0) {
      // Extract what comes after -pr
      const suffix = firstPart.substring(prIndex + 3) // skip "-pr"

      // Check if suffix is all digits (PR number)
      if (suffix.length > 0 && /^\d+$/.test(suffix)) {
        // Extract partner name (everything before -prNNN)
        const partnerName = firstPart.substring(0, prIndex)
        // Reconstruct domain with original partner name
        const transformedParts = [partnerName, ...parts.slice(1)]
        return transformedParts.join('.')
      }
    }
  }

  // OLD FORMAT: Nested subdomain preview domains (5+ parts)
  // Pattern: partner.pr-<number>.staging.alternativepayments.io
  // Example: ribeiro.pr-3187.staging.alternativepayments.io
  // Note: Keeping for backwards compatibility during transition
  if (parts.length >= 5 && parts[1].startsWith('pr-')) {
    // Remove the pr-<number> part by excluding parts[1]
    const transformedParts = [parts[0], ...parts.slice(2)]
    return transformedParts.join('.')
  }

  return domain
}

/**
 * Transform a payment portal URL to use the preview domain pattern.
 *
 * Takes a regular payment portal URL and injects the preview PR number
 * to create the preview-specific URL.
 *
 * @param paymentsPortalUrl - The partner's payment portal URL (e.g., "ribeiro.staging.alternativepayments.io")
 * @param currentHost - The current hostname (e.g., "pr-3171.app.staging.alternativepayments.io")
 * @returns Transformed URL for preview environment or original URL with https://
 *
 * @example
 * // From partner preview, generate payments preview URL
 * transformPaymentPortalUrl('ribeiro.staging.alternativepayments.io', 'pr-3171.app.staging.alternativepayments.io')
 * // Returns: 'https://ribeiro-pr3171.staging.alternativepayments.io'
 *
 * @example
 * // Non-preview environment
 * transformPaymentPortalUrl('ribeiro.staging.alternativepayments.io', 'app.staging.alternativepayments.io')
 * // Returns: 'https://ribeiro.staging.alternativepayments.io'
 */
export const transformPaymentPortalUrl = (
  paymentsPortalUrl: string,
  currentHost: string
): string => {
  // Check if we're in a preview environment (partner app pattern)
  const prNumber = getPreviewPRNumber(currentHost)

  if (!prNumber) {
    // Not a preview environment, return original URL with https://
    return `https://${paymentsPortalUrl}`
  }

  // Extract partner subdomain from paymentsPortalUrl
  // Example: "ribeiro.staging.alternativepayments.io" → "ribeiro"
  const partnerSubdomain = paymentsPortalUrl.split('.')[0]

  // Build preview URL using new hyphenated format: ribeiro-pr3171.staging.alternativepayments.io
  return `https://${partnerSubdomain}-pr${prNumber}.staging.alternativepayments.io`
}
